'use client';

import { useCallback, type ChangeEvent, type MutableRefObject } from 'react';
import { getNodesBounds, type ReactFlowInstance } from '@xyflow/react';

import { exportMindmapToList, exportMindmapToText } from '@/features/mindmap/core/MindmapText';
import { parseMindmapImport, serializeMindmapForDownload } from '@/features/mindmap/core/MindmapPersistence';
import type { Mindmap } from '@/features/mindmap/core/MindmapTypes';

interface UseMindmapIOOptions {
  doc: Mindmap;
  resetHistory: (next: Mindmap) => void;
  logger: { error: (...args: any[]) => void };
  hideToolbar: () => void;
  reactFlow: ReactFlowInstance;
  canvasRef: MutableRefObject<HTMLDivElement | null>;
  fileInputRef: MutableRefObject<HTMLInputElement | null>;
  onAfterImport?: (imported: Mindmap) => void;
}

interface UseMindmapIOResult {
  exportAsMindmapText: () => Promise<void>;
  exportAsList: () => Promise<void>;
  exportAsPng: () => Promise<void>;
  importFromFile: (event: ChangeEvent<HTMLInputElement>) => Promise<void>;
  downloadSnapshot: () => void;
  triggerImportDialog: () => void;
}

export function useMindmapIO({
  doc,
  resetHistory,
  logger,
  hideToolbar,
  reactFlow,
  canvasRef,
  fileInputRef,
  onAfterImport,
}: UseMindmapIOOptions): UseMindmapIOResult {
  const exportAsMindmapText = useCallback(async () => {
    try {
      const text = exportMindmapToText(doc);
      // Test seam: allow UI tests to capture clipboard without requiring navigator.clipboard
      if (typeof window !== 'undefined') {
        const w: any = window as any;
        if (typeof w.__captureClipboard === 'function') {
          await w.__captureClipboard(text);
          return;
        }
        if ('__copied' in w) {
          w.__copied = text;
          return;
        }
      }
      if (text && navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
      }
      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${doc.meta?.title ?? doc.id ?? 'mindmap'}.mindmap.txt`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      logger.error('mindmap:export:text', error);
    }
  }, [doc, logger]);

  const exportAsList = useCallback(async () => {
    try {
      const text = exportMindmapToList(doc);
      if (!text) return;
      // Test seam: allow UI tests to capture clipboard without requiring navigator.clipboard
      if (typeof window !== 'undefined') {
        const w: any = window as any;
        if (typeof w.__captureClipboard === 'function') {
          await w.__captureClipboard(text);
          return;
        }
        if ('__copied' in w) {
          w.__copied = text;
          return;
        }
      }
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
      }
      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${doc.meta?.title ?? doc.id ?? 'mindmap'}.list.txt`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      logger.error('mindmap:export:list', error);
    }
  }, [doc, logger]);

  const exportAsPng = useCallback(async () => {
    const container = canvasRef.current;
    if (!container) return;
    hideToolbar();

    const edgeAttrAdjustments: Array<{
      el: SVGElement;
      prevStroke: string | null;
      prevStrokeWidth: string | null;
      prevFill: string | null;
    }> = [];
    const svgSizeAdjustments: Array<{
      el: SVGSVGElement;
      prevWidthAttr: string | null;
      prevHeightAttr: string | null;
      prevStyleWidth: string;
      prevStyleHeight: string;
    }> = [];

    try {
      // Test seam: allow tests to inject a mock toPng without touching ESM namespace
      let toPng: (node: HTMLElement, options: any) => Promise<string>;
      const maybeInjected =
        typeof window !== 'undefined' && (window as any)?.__htmlToImage_toPng;
      if (typeof maybeInjected === 'function') {
        toPng = maybeInjected as typeof toPng;
      } else {
        ({ toPng } = await import('html-to-image'));
      }

      const reactFlowWrapper = container.querySelector('.react-flow') as HTMLElement;
      if (!reactFlowWrapper) throw new Error('Could not find React Flow element');
      const viewportElement = reactFlowWrapper.querySelector('.react-flow__viewport') as HTMLElement | null;
      if (!viewportElement) throw new Error('Could not find React Flow viewport element');

      const isDark =
        document.documentElement.classList.contains('dark') ||
        document.body.classList.contains('dark') ||
        window.matchMedia?.('(prefers-color-scheme: dark)').matches;
      const darkBg = getComputedStyle(document.body).backgroundColor || '#0d0f12';
      const backgroundColor = isDark ? darkBg : '#ffffff';
      const isLightBg = backgroundColor === '#ffffff' || /rgb\(\s*255\s*,\s*255\s*,\s*255\s*\)/.test(backgroundColor);

      reactFlowWrapper
        .querySelectorAll(
          '.react-flow__edges path, .react-flow__edges line, .react-flow__edges polyline, .react-flow__edges circle, .react-flow__edges ellipse'
        )
        .forEach((node) => {
          const el = node as SVGElement;
          edgeAttrAdjustments.push({
            el,
            prevStroke: el.getAttribute('stroke'),
            prevStrokeWidth: el.getAttribute('stroke-width'),
            prevFill: el.getAttribute('fill'),
          });
          const cs = window.getComputedStyle(el);
          let stroke = cs.stroke;
          let strokeWidth = cs.strokeWidth;
          const fill = cs.fill;
          const isTransparent =
            !stroke || stroke === 'none' || /rgba?\(\s*0\s*,\s*0\s*,\s*0\s*(,\s*0\s*)?\)/.test(stroke);
          const isWhite = stroke === '#fff' || /rgb\(\s*255\s*,\s*255\s*,\s*255\s*\)/.test(stroke);
          const fallbackStroke = isLightBg ? '#1f2937' : '#e5e7eb';
          if (isTransparent || (isLightBg && isWhite)) stroke = fallbackStroke;
          if (!strokeWidth || strokeWidth === '0' || strokeWidth === '0px') strokeWidth = '2px';

          if (stroke && stroke !== 'none') el.setAttribute('stroke', stroke);
          if (strokeWidth) el.setAttribute('stroke-width', strokeWidth);
          if (fill && fill !== 'none') el.setAttribute('fill', fill);
        });

      viewportElement.querySelectorAll('svg').forEach((node) => {
        const el = node as SVGSVGElement;
        const viewBox = el.getAttribute('viewBox');
        let width: number | null = null;
        let height: number | null = null;
        if (viewBox) {
          const parts = viewBox.trim().split(/\s+/);
          if (parts.length === 4) {
            width = Math.max(1, Math.round(parseFloat(parts[2])));
            height = Math.max(1, Math.round(parseFloat(parts[3])));
          }
        }
        if (width == null || height == null) {
          const rect = el.getBoundingClientRect();
          width = Math.max(1, Math.round(rect.width || 1));
          height = Math.max(1, Math.round(rect.height || 1));
        }
        svgSizeAdjustments.push({
          el,
          prevWidthAttr: el.getAttribute('width'),
          prevHeightAttr: el.getAttribute('height'),
          prevStyleWidth: el.style.width,
          prevStyleHeight: el.style.height,
        });
        el.setAttribute('width', String(width));
        el.setAttribute('height', String(height));
        el.style.width = `${width}px`;
        el.style.height = `${height}px`;
      });

      const filter = (node: HTMLElement) => {
        if (
          node.classList?.contains('react-flow__minimap') ||
          node.classList?.contains('react-flow__controls') ||
          node.classList?.contains('react-flow__background') ||
          node.classList?.contains('react-flow__panel') ||
          node.getAttribute?.('data-testid') === 'mindmap-legend' ||
          node.getAttribute?.('data-testid') === 'mindmap-mode' ||
          node.classList?.contains('mindmap-lasso-overlay')
        ) {
          return false;
        }
        return true;
      };

      const nodesForBounds = (reactFlow.getNodes?.() as any[]) || [];
      const bounds = getNodesBounds(nodesForBounds as any);
      const bleedLeft = 0.08;
      const bleedRight = 0.24;
      const bleedTop = 0.12;
      const bleedBottom = 0.2;
      const minMarginX = 80;
      const minMarginY = 120;
      const leftPad = Math.max(minMarginX, Math.round(bounds.width * bleedLeft));
      const rightPad = Math.max(minMarginX, Math.round(bounds.width * bleedRight));
      const topPad = Math.max(minMarginY, Math.round(bounds.height * bleedTop));
      const bottomPad = Math.max(minMarginY, Math.round(bounds.height * bleedBottom));
      const expandedBounds = {
        x: bounds.x - leftPad,
        y: bounds.y - topPad,
        width: bounds.width + leftPad + rightPad,
        height: bounds.height + topPad + bottomPad,
      } as typeof bounds;
      const wrapperRect = reactFlowWrapper.getBoundingClientRect();
      const exportWidth = Math.max(1, Math.ceil(expandedBounds.width || wrapperRect.width || 1024));
      const exportHeight = Math.max(1, Math.ceil(expandedBounds.height || wrapperRect.height || 768));
      const translateX = -Math.round(expandedBounds.x);
      const translateY = -Math.round(expandedBounds.y);

      const dataUrl = await toPng(viewportElement, {
        backgroundColor,
        cacheBust: true,
        pixelRatio: 2,
        width: exportWidth,
        height: exportHeight,
        style: {
          width: `${exportWidth}px`,
          height: `${exportHeight}px`,
          transform: `translate(${translateX}px, ${translateY}px) scale(1)`,
          transformOrigin: '0 0',
        },
        filter,
      });

      const link = document.createElement('a');
      link.href = dataUrl;
      const baseName = (doc.meta?.title ?? doc.id ?? 'mindmap').toString().trim() || 'mindmap';
      link.download = `${baseName}.png`;
      link.click();
    } catch (error) {
      logger.error('mindmap:export:png', error);
    } finally {
      edgeAttrAdjustments.forEach(({ el, prevStroke, prevStrokeWidth, prevFill }) => {
        if (prevStroke == null) el.removeAttribute('stroke');
        else el.setAttribute('stroke', prevStroke);
        if (prevStrokeWidth == null) el.removeAttribute('stroke-width');
        else el.setAttribute('stroke-width', prevStrokeWidth);
        if (prevFill == null) el.removeAttribute('fill');
        else el.setAttribute('fill', prevFill);
      });
      svgSizeAdjustments.forEach(({ el, prevWidthAttr, prevHeightAttr, prevStyleWidth, prevStyleHeight }) => {
        if (prevWidthAttr == null) el.removeAttribute('width');
        else el.setAttribute('width', prevWidthAttr);
        if (prevHeightAttr == null) el.removeAttribute('height');
        else el.setAttribute('height', prevHeightAttr);
        el.style.width = prevStyleWidth;
        el.style.height = prevStyleHeight;
      });
    }
  }, [canvasRef, doc, hideToolbar, logger, reactFlow]);

  const importFromFile = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        const imported = parseMindmapImport(text);
        resetHistory(imported);
        onAfterImport?.(imported);
      } catch (error) {
        logger.error('mindmap:import', error);
        if (typeof window !== 'undefined' && typeof window.alert === 'function') {
          window.alert('Unable to import mindmap. Please select a valid export file.');
        }
      } finally {
        event.target.value = '';
      }
    },
    [logger, onAfterImport, resetHistory]
  );

  const downloadSnapshot = useCallback(() => {
    try {
      const snapshot = serializeMindmapForDownload(doc);
      const blob = new Blob([snapshot], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const baseName = (doc.meta?.title ?? doc.id ?? 'mindmap').toString().trim() || 'mindmap';
      link.href = url;
      link.download = `${baseName}.mmp`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      logger.error('mindmap:download', error);
      if (typeof window !== 'undefined' && typeof window.alert === 'function') {
        window.alert('Unable to download mindmap.');
      }
    }
  }, [doc, logger]);

  const triggerImportDialog = useCallback(() => {
    fileInputRef.current?.click();
  }, [fileInputRef]);

  return {
    exportAsMindmapText,
    exportAsList,
    exportAsPng,
    importFromFile,
    downloadSnapshot,
    triggerImportDialog,
  };
}

export default useMindmapIO;
